/**
 * Lista de Clientes Pendentes de Feedback
 * Exibe clientes que precisam ser contatados e permite registrar feedbacks
 */
import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Spinner, Alert, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { fetchPendentesWinback, atualizarFeedbackEmLote } from '../../services/api';
import RegistrarFeedbackModal from './RegistrarFeedbackModal';
import './ListaPendentes.css';

const RESULTADOS_RAPIDOS = [
    { key: 'nao_atendeu', label: 'Não Atendeu', emoji: '📵' },
    { key: 'ligar_depois', label: 'Ligar Depois', emoji: '⏳' },
    { key: 'numero_invalido', label: 'Inválido', emoji: '❌' }
];

const ListaPendentes = ({ vendedora = '', onFeedbackRegistrado }) => {
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [clienteSelecionado, setClienteSelecionado] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selecionados, setSelecionados] = useState([]);
    const [atualizandoLote, setAtualizandoLote] = useState(false);

    useEffect(() => {
        carregarDados();
    }, [vendedora]);

    const carregarDados = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchPendentesWinback(vendedora, 100);
            if (response.success) {
                setClientes(response.data || []);
            } else {
                setError(response.message);
            }
        } catch (err) {
            setError('Erro ao carregar dados');
            console.error('[ListaPendentes] Erro:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAbrirModal = (cliente) => {
        setClienteSelecionado(cliente);
        setShowModal(true);
    };

    const handleFeedbackSucesso = (cliente, resultado) => {
        // Remover cliente da lista
        setClientes(prev => prev.filter(c => c.id !== cliente.id));
        setSelecionados(prev => prev.filter(id => id !== cliente.id));
        onFeedbackRegistrado && onFeedbackRegistrado();
    };

    const toggleSelecao = (id) => {
        setSelecionados(prev =>
            prev.includes(id)
                ? prev.filter(i => i !== id)
                : [...prev, id]
        );
    };

    const toggleTodos = () => {
        if (selecionados.length === clientes.length) {
            setSelecionados([]);
        } else {
            setSelecionados(clientes.map(c => c.id));
        }
    };

    const handleAtualizarLote = async (idsOuResultado, resultadoOpcional) => {
        // Suporta duas formas de chamada:
        // 1. handleAtualizarLote('resultado') - usa selecionados
        // 2. handleAtualizarLote([ids], 'resultado') - usa ids específicos
        let ids, resultado;

        if (Array.isArray(idsOuResultado)) {
            ids = idsOuResultado;
            resultado = resultadoOpcional;
        } else {
            ids = selecionados;
            resultado = idsOuResultado;
        }

        if (ids.length === 0) return;

        setAtualizandoLote(true);
        try {
            const response = await atualizarFeedbackEmLote(ids, resultado);
            if (response.success) {
                // Remover clientes processados
                setClientes(prev => prev.filter(c => !ids.includes(c.id)));
                setSelecionados(prev => prev.filter(id => !ids.includes(id)));
                onFeedbackRegistrado && onFeedbackRegistrado();
            }
        } catch (err) {
            console.error('[ListaPendentes] Erro ao atualizar em lote:', err);
        } finally {
            setAtualizandoLote(false);
        }
    };

    const formatarTelefone = (telefone) => {
        if (!telefone) return '-';
        return telefone.replace(/\D/g, '');
    };

    if (loading) {
        return (
            <div className="pendentes-loading">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Carregando clientes pendentes...</p>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div className="lista-pendentes">
            {/* Header */}
            <div className="pendentes-header">
                <div className="pendentes-title">
                    <h5 className="mb-0">📋 Clientes Pendentes</h5>
                    <Badge bg="primary" className="ms-2">{clientes.length}</Badge>
                </div>
                <Button variant="outline-primary" size="sm" onClick={carregarDados}>
                    🔄 Atualizar
                </Button>
            </div>

            {/* Ações em Lote */}
            {selecionados.length > 0 && (
                <div className="lote-actions">
                    <span className="lote-info">
                        <strong>{selecionados.length}</strong> selecionado(s)
                    </span>
                    <div className="lote-buttons">
                        {RESULTADOS_RAPIDOS.map(r => (
                            <OverlayTrigger
                                key={r.key}
                                placement="top"
                                overlay={<Tooltip>{r.label}</Tooltip>}
                            >
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => handleAtualizarLote(r.key)}
                                    disabled={atualizandoLote}
                                >
                                    {r.emoji}
                                </Button>
                            </OverlayTrigger>
                        ))}
                    </div>
                </div>
            )}

            {clientes.length === 0 ? (
                <Card className="pendentes-empty">
                    <Card.Body className="text-center py-5">
                        <div style={{ fontSize: '4rem' }}>🎉</div>
                        <h5 className="mt-3">Nenhum cliente pendente!</h5>
                        <p className="text-muted mb-0">
                            Distribua novos clientes para começar as ligações.
                        </p>
                    </Card.Body>
                </Card>
            ) : (
                <Card className="pendentes-table-card">
                    <div className="table-responsive">
                        <Table hover className="pendentes-table mb-0">
                            <thead>
                                <tr>
                                    <th className="th-check">
                                        <Form.Check
                                            type="checkbox"
                                            checked={selecionados.length === clientes.length}
                                            onChange={toggleTodos}
                                        />
                                    </th>
                                    <th>Cliente</th>
                                    <th>Telefone</th>
                                    <th>Cidade</th>
                                    <th>Dias</th>
                                    <th>Vendedora</th>
                                    <th className="th-actions">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clientes.map(cliente => (
                                    <tr key={cliente.id} className={selecionados.includes(cliente.id) ? 'selected' : ''}>
                                        <td>
                                            <Form.Check
                                                type="checkbox"
                                                checked={selecionados.includes(cliente.id)}
                                                onChange={() => toggleSelecao(cliente.id)}
                                            />
                                        </td>
                                        <td className="td-cliente">
                                            <span className="cliente-nome">{cliente.cliente_nome || 'Desconhecido'}</span>
                                            {cliente.cliente_email && (
                                                <small className="cliente-email">{cliente.cliente_email}</small>
                                            )}
                                        </td>
                                        <td>
                                            {cliente.cliente_telefone ? (
                                                <a
                                                    href={`https://wa.me/55${formatarTelefone(cliente.cliente_telefone)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="telefone-link"
                                                >
                                                    📱 {cliente.cliente_telefone}
                                                </a>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>{cliente.cliente_cidade || '-'}</td>
                                        <td>
                                            <Badge
                                                bg={cliente.dias_sem_comprar > 90 ? 'danger' : cliente.dias_sem_comprar > 60 ? 'warning' : 'success'}
                                            >
                                                {cliente.dias_sem_comprar}d
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg="secondary">{cliente.vendedora}</Badge>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleAbrirModal(cliente)}
                                                >
                                                    📞 Registrar
                                                </Button>
                                                {RESULTADOS_RAPIDOS.slice(0, 2).map(r => (
                                                    <OverlayTrigger
                                                        key={r.key}
                                                        placement="top"
                                                        overlay={<Tooltip>{r.label}</Tooltip>}
                                                    >
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => handleAtualizarLote([cliente.id], r.key)}
                                                        >
                                                            {r.emoji}
                                                        </Button>
                                                    </OverlayTrigger>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card>
            )}

            {/* Modal de Feedback */}
            <RegistrarFeedbackModal
                show={showModal}
                onHide={() => setShowModal(false)}
                cliente={clienteSelecionado}
                onSuccess={handleFeedbackSucesso}
            />
        </div>
    );
};

export default ListaPendentes;
